/**
 * Reusable Column Visibility Dropdown Component
 * Used for showing/hiding table columns
 */
import React from 'react';
import ReactDOM from 'react-dom';

function viewportFrame() {
  const visual = window.visualViewport;
  return {
    left: visual?.offsetLeft ?? 0,
    top: visual?.offsetTop ?? 0,
    width: visual?.width ?? window.innerWidth,
    height: visual?.height ?? window.innerHeight,
  };
}

/** Keep a fixed menu inside the visible screen, including short tablet layouts. */
function fitMenuToViewport(menu) {
  if (!menu) return;
  const margin = 8;
  const frame = viewportFrame();
  const maxWidth = Math.max(160, frame.width - margin * 2);
  menu.style.maxWidth = `${maxWidth}px`;
  menu.style.width = menu.scrollWidth > maxWidth ? `${maxWidth}px` : "";

  const rect = menu.getBoundingClientRect();
  let left = rect.left;
  let top = rect.top;
  const maxRight = frame.left + frame.width - margin;
  const maxBottom = frame.top + frame.height - margin;

  if (rect.right > maxRight) left -= rect.right - maxRight;
  if (left < frame.left + margin) left = frame.left + margin;

  if (rect.bottom > maxBottom) top -= rect.bottom - maxBottom;
  if (top < frame.top + margin) top = frame.top + margin;

  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;

  const list = menu.querySelector("[data-column-menu-list]");
  const header = menu.querySelector("[data-column-menu-header]");
  if (!list) return;
  const headerHeight = header?.offsetHeight ?? 0;
  const available = maxBottom - top - headerHeight - margin;
  const compactListHeight = 256;
  list.style.maxHeight = `${Math.max(96, Math.min(compactListHeight, available))}px`;
}

export const ColumnVisibilityDropdown = React.forwardRef(({
  isOpen,
  onClose,
  columns,
  visibleColumns,
  onToggleColumn,
  position,
  requiredKeys = [],
  groupBy = null,
  onReorderColumn,
  onReorderEnd
}, ref) => {
  const [isEditingOrder, setIsEditingOrder] = React.useState(false);
  const draggedIndexRef = React.useRef(null);
  const draggedGroupIndexRef = React.useRef(null);
  const menuRef = React.useRef(null);

  const setMenuRef = React.useCallback((node) => {
    menuRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) ref.current = node;
  }, [ref]);

  React.useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const fit = () => fitMenuToViewport(menuRef.current);
    fit();
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    const visual = window.visualViewport;
    visual?.addEventListener("resize", fit);
    visual?.addEventListener("scroll", fit);
    return () => {
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
      visual?.removeEventListener("resize", fit);
      visual?.removeEventListener("scroll", fit);
    };
  }, [isOpen, position?.top, position?.left, isEditingOrder, columns?.length]);

  if (!isOpen) return null;

  // Group columns if groupBy function is provided
  let columnGroups = [{ title: null, columns: columns }];

  if (groupBy) {
    const grouped = groupBy(columns);
    columnGroups = Object.entries(grouped).map(([title, cols]) => ({
      title,
      columns: cols
    }));
  }

  const handleDragStart = (e, index, groupIdx) => {
    draggedIndexRef.current = index;
    draggedGroupIndexRef.current = groupIdx;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e, targetIndex, groupIdx, groupCols) => {
    e.preventDefault();
    const draggedIndex = draggedIndexRef.current;
    const draggedGroupIdx = draggedGroupIndexRef.current;

    if (draggedIndex === null || draggedGroupIdx === null || draggedGroupIdx !== groupIdx) return;
    if (draggedIndex === targetIndex) return;

    if (onReorderColumn) {
      onReorderColumn(draggedIndex, targetIndex, groupCols);
    }
    draggedIndexRef.current = targetIndex;
  };

  const handleDragEnd = () => {
    draggedIndexRef.current = null;
    draggedGroupIndexRef.current = null;
    if (onReorderEnd) {
      onReorderEnd();
    }
  };

  const dropdown = (
    <div
      ref={setMenuRef}
      className="w-52 max-w-[calc(100vw-16px)] bg-white border rounded-md shadow-lg py-2 text-xs"
      style={{
        position: 'fixed',
        top: `${position?.top ?? 0}px`,
        left: `${position?.left ?? 0}px`,
        zIndex: 9999,
        transform: 'none',
        boxSizing: 'border-box',
        fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
      }}
    >
      <div data-column-menu-header className="px-4 py-2 border-b flex items-center justify-between gap-2">
        <h3 className="min-w-0 truncate text-xs font-semibold text-gray-700">Show/Hide Columns</h3>
        {onReorderColumn && (
          <button
            type="button"
            onClick={() => setIsEditingOrder(!isEditingOrder)}
            className="text-[10px] text-red-600 hover:text-red-700 font-medium px-2 py-0.5 border border-red-200 rounded hover:bg-red-50 transition-colors"
          >
            {isEditingOrder ? 'Done' : 'Edit'}
          </button>
        )}
      </div>
      <div data-column-menu-list className="max-h-64 overflow-y-auto">
        {columnGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            {group.title && (
              <>
                {groupIndex > 0 && <div className="border-t border-gray-200 my-2"></div>}
                <div className="px-4 py-1">
                  <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {group.title}
                  </h4>
                </div>
              </>
            )}
            {group.columns.map((column, index) => (
              <div
                key={column.key}
                className={`flex items-center px-4 py-1.5 hover:bg-gray-50 transition-all select-none ${isEditingOrder ? 'border-b border-gray-100 cursor-grab active:cursor-grabbing' : ''
                  }`}
                draggable={isEditingOrder}
                onDragStart={(e) => handleDragStart(e, index, groupIndex)}
                onDragOver={(e) => handleDragOver(e, index, groupIndex, group.columns)}
                onDragEnd={handleDragEnd}
              >
                {isEditingOrder && (
                  <div className="text-gray-400 mr-2 flex items-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 2a2 2 0 11.001 3.999A2 2 0 017 2zm0 6a2 2 0 11.001 3.999A2 2 0 017 8zm0 6a2 2 0 11.001 3.999A2 2 0 017 14zm6-12a2 2 0 11.001 3.999A2 2 0 0113 2zm0 6a2 2 0 11.001 3.999A2 2 0 0113 8zm0 6a2 2 0 11.001 3.999A2 2 0 0113 14z" />
                    </svg>
                  </div>
                )}
                <label className="flex items-center cursor-pointer flex-grow py-0.5">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(column.key)}
                    onChange={() => onToggleColumn(column.key)}
                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                    disabled={requiredKeys.includes(column.key)}
                  />
                  <span className="ml-2 min-w-0 text-xs text-gray-700">{column.label}</span>
                </label>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );

  // Portal to documentElement to avoid zoom issues on body
  return ReactDOM.createPortal(dropdown, document.documentElement);
});

ColumnVisibilityDropdown.displayName = 'ColumnVisibilityDropdown';

